import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COUNTRIES, Country } from '@/types/user';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface CountryDropdownProps {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When true, shows a small "Clear" affordance and treats '' as a valid value. */
  allowClear?: boolean;
  /** className for the trigger button. */
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  /** 'down' or 'up' — which direction the panel opens. */
  direction: 'down' | 'up';
}

const triggerBase =
  'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Custom country dropdown.
 *
 * Uses createPortal to escape the Dialog's overflow container, and computes
 * its own position from the trigger's bounding rect so it can flip up if
 * there's not enough room below. Hard max-height + internal scroll.
 */
export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  value,
  onChange,
  placeholder = 'Select country...',
  disabled,
  allowClear = false,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<PanelPosition | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === value) ?? null,
    [value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  // Compute panel position relative to the trigger. Re-run on open, scroll,
  // resize, and when the trigger moves.
  const recomputePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 4;
    const panelMax = 280; // max-height of the panel (px)
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const direction: 'down' | 'up' =
      spaceBelow >= Math.min(panelMax, 220) || spaceBelow >= spaceAbove
        ? 'down'
        : 'up';
    const top =
      direction === 'down'
        ? Math.min(rect.bottom + margin, vh - panelMax - margin)
        : Math.max(rect.top - margin - panelMax, margin);
    setPos({
      top,
      left: Math.max(margin, rect.left),
      width: rect.width,
      direction,
    });
  }, []);

  // Open / close
  useEffect(() => {
    if (!open) return;
    recomputePosition();
    setQuery('');
    setActiveIndex(0);
    // Defer focus until after the panel mounts.
    const t = window.setTimeout(() => searchRef.current?.focus(), 10);
    return () => window.clearTimeout(t);
  }, [open, recomputePosition]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const onScroll = () => recomputePosition();
    const onResize = () => recomputePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, recomputePosition]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clamp active index when filter results change
  useLayoutEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIndex]);

  const selectCode = (code: string) => {
    onChange(code);
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  const clear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  const onKeyDownTrigger = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[activeIndex] ?? filtered[0];
      if (target) selectCode(target.code);
    } else if (e.key === 'Tab') {
      // Close on tab so focus can move naturally to the next field.
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDownTrigger}
        className={cn(triggerBase, 'group', className)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">
          {selected ? (
            <>
              <CountryFlag countryCode={selected.code} size="sm" />
              <span className="truncate">{selected.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {allowClear && value && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') clear(e);
              }}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear country"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 opacity-50 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          id={listboxId}
          aria-label={ariaLabel ?? 'Country options'}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 300,
            maxHeight: 280,
          }}
          className={cn(
            'flex flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
          )}
        >
          <div className="flex items-center gap-2 border-b border-border bg-popover px-3 py-2">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDownSearch}
              placeholder="Search country..."
              className="h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div
            className="flex-1 overflow-y-auto overscroll-contain py-1"
            style={{ maxHeight: 232 }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No country found.
              </div>
            ) : (
              filtered.map((c, i) => {
                const isSelected = c.code === value;
                const isActive = i === activeIndex;
                return (
                  <CountryOption
                    key={c.code}
                    country={c}
                    isSelected={isSelected}
                    isActive={isActive}
                    onSelect={() => selectCode(c.code)}
                    onHover={() => setActiveIndex(i)}
                  />
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

const CountryOption: React.FC<{
  country: Country;
  isSelected: boolean;
  isActive: boolean;
  onSelect: () => void;
  onHover: () => void;
}> = ({ country, isSelected, isActive, onSelect, onHover }) => (
  <div
    role="option"
    aria-selected={isSelected}
    onMouseDown={(e) => {
      // Use onMouseDown to fire before the outside-click listener on document.
      e.preventDefault();
      onSelect();
    }}
    onMouseMove={onHover}
    className={cn(
      'flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm',
      isActive && 'bg-accent text-accent-foreground',
    )}
  >
    <span className="flex min-w-0 items-center gap-2">
      <CountryFlag countryCode={country.code} size="sm" />
      <span className="truncate">{country.name}</span>
    </span>
    {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
  </div>
);

export default CountryDropdown;
